import { TreeDataItem as BaseTreeDataItem } from "@/components/tree-view";
import { GoogleCalendarCalendarEventEntity } from "@cohesive/api-entities";
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
  startOfWeek,
} from "date-fns";
import { Calendar, LucideProps, Star } from "lucide-react";
import React from "react";

// Use type alias instead of interface extension to avoid type conflicts
export type TreeDataItem = BaseTreeDataItem & {
  calendarType?: "primary" | "secondary";
  calendarId?: string;
};

// Define the type for TreeSVGIcon to match the TreeView component
export type TreeSVGIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

export type CalendarTabConfig = {
  type: "calendar";
  calendarId: string;
  calendarName: string;
  icon: React.ReactNode;
};

export type TabConfig = CalendarTabConfig;

export const getCalendarIcon = (
  isPrimary?: boolean,
): React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
> => {
  return isPrimary ? Star : Calendar;
};

// Interface for calendar day view
export interface CalendarDay {
  date: Date;
  dateKey: string; // ISO date string (YYYY-MM-DD)
  displayName: string; // "Today", "Tomorrow", "Yesterday", or formatted date
  shortName: string; // "Mon 15", "Tue 16", etc.
  events: GoogleCalendarCalendarEventEntity[];
  isToday: boolean;
  isSelected: boolean;
}

// Interface for positioned events in day view
export interface PositionedEvent {
  event: GoogleCalendarCalendarEventEntity;
  startTime: Date;
  endTime: Date;
  top: number; // percentage from top of day
  height: number; // percentage of day height
  startTimeDisplay: string;
  endTimeDisplay: string;
  isAllDay: boolean;
}

// Helper function to get display name for a date
export function getDateDisplayName(date: Date): string {
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "EEEE, MMMM d"); // e.g., "Monday, January 15"
}

// Helper function to get short display name for day navigation
export function getShortDateName(date: Date): string {
  return format(date, "EEE d"); // e.g., "Mon 15"
}

// Helper function to get week days around a selected date
export function getWeekDaysAround(selectedDate: Date): CalendarDay[] {
  const startOfCurrentWeek = startOfWeek(selectedDate, { weekStartsOn: 1 }); // Monday start
  const endOfCurrentWeek = endOfWeek(selectedDate, { weekStartsOn: 1 });

  const days = eachDayOfInterval({
    start: startOfCurrentWeek,
    end: endOfCurrentWeek,
  });

  return days.map((date) => ({
    date,
    dateKey: format(date, "yyyy-MM-dd"),
    displayName: getDateDisplayName(date),
    shortName: getShortDateName(date),
    events: [],
    isToday: isToday(date),
    isSelected: isSameDay(date, selectedDate),
  }));
}

// Helper function to position events in day view (24-hour timeline)
export function positionEventsInDay(
  events: GoogleCalendarCalendarEventEntity[],
): PositionedEvent[] {
  const positioned: PositionedEvent[] = [];

  events.forEach((event) => {
    const startDateTime = event.data.start_date_time;
    const endDateTime = event.data.end_date_time;

    if (!startDateTime) return;

    try {
      const startTime = parseISO(startDateTime);
      const endTime = endDateTime
        ? parseISO(endDateTime)
        : addDays(startTime, 0);

      // Check if it's an all-day event (no time component or spans full day)
      const isAllDay =
        !startDateTime.includes("T") ||
        endTime.getTime() - startTime.getTime() >= 24 * 60 * 60 * 1000;

      if (isAllDay) {
        positioned.push({
          event,
          startTime,
          endTime,
          top: 0,
          height: 100,
          startTimeDisplay: "All day",
          endTimeDisplay: "",
          isAllDay: true,
        });
      } else {
        // Calculate position based on 24-hour timeline
        const startHour = startTime.getHours() + startTime.getMinutes() / 60;
        const endHour = endTime.getHours() + endTime.getMinutes() / 60;

        const top = (startHour / 24) * 100;
        const height = Math.max(((endHour - startHour) / 24) * 100, 2); // Minimum 2% height

        positioned.push({
          event,
          startTime,
          endTime,
          top,
          height,
          startTimeDisplay: format(startTime, "h:mm a"),
          endTimeDisplay: format(endTime, "h:mm a"),
          isAllDay: false,
        });
      }
    } catch (error) {
      console.warn("Invalid date in event:", event.data.id, startDateTime);
    }
  });

  return positioned.sort((a, b) => {
    // Sort by start time, all-day events first
    if (a.isAllDay && !b.isAllDay) return -1;
    if (!a.isAllDay && b.isAllDay) return 1;
    return a.startTime.getTime() - b.startTime.getTime();
  });
}

// Helper function to filter events for a specific day
export function getEventsForDay(
  events: GoogleCalendarCalendarEventEntity[],
  date: Date,
): GoogleCalendarCalendarEventEntity[] {
  const targetDay = format(date, "yyyy-MM-dd");

  return events.filter((event) => {
    const startDateTime = event.data.start_date_time;
    if (!startDateTime) return false;

    try {
      const eventDate = parseISO(startDateTime);
      const eventDay = format(eventDate, "yyyy-MM-dd");
      return eventDay === targetDay;
    } catch (error) {
      return false;
    }
  });
}

// Helper function to get time range text for an event
export function formatEventTimeRange(
  startDateTime?: string,
  endDateTime?: string,
): string {
  if (!startDateTime) return "";

  try {
    const start = parseISO(startDateTime);
    const startTime = format(start, "h:mm a");

    if (!endDateTime) return startTime;

    const end = parseISO(endDateTime);
    const endTime = format(end, "h:mm a");

    // Check if it's the same day
    if (isSameDay(start, end)) {
      return `${startTime} - ${endTime}`;
    } else {
      // Multi-day event
      return `${format(start, "MMM d, h:mm a")} - ${format(end, "MMM d, h:mm a")}`;
    }
  } catch (error) {
    return startDateTime; // Fallback to raw string
  }
}
